import { useMutation } from "@apollo/client";
import { Button, Collapse, Drawer, Form, Select, message } from "antd";
import dayjs from "dayjs";
import { capitalize, isEmpty } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserContext } from "../../../amplify/authenticator-provider";
import { CREATE_NOTE, UPDATE_NOTE } from "../../../graphql/mutation";
import useCrmIntegration from "../../../hooks/useCrmIntegration";
import { convertUtcToLocal, uploadFilesFromBlobUrls } from "../../../library/utils";
import { getMobileView } from "../../../redux/slices/isMobile";
import RichTextEditorWithAudioRecorder from "./RichTextEditorWithAudioRecorder";
import AudioPlayer from "./audioPlayer";
import { OppId } from "./engagementHelpers";
import { addNote, updateLeadNoteRedux } from "../../../redux/slices/notesSlice";

export function NotesDrawer({ leadNotesData, setleadNotesData, refetchLeadNotes }) {
  const { mode, leadNote } = leadNotesData;
  const initialNotes = {
    noteType: "OPPORTUNITY",
    note: mode === "EDIT" ? leadNote.note : "",
  };
  const { selectedLead } = useSelector(state => state.lead);
  const dispatch = useDispatch();
  const isMobileView = useSelector(state => getMobileView(state));
  const { user } = useContext(UserContext);
  const [form] = Form.useForm();
  const [formChanged, setformChanged] = useState(false);
  const crmIntegration = useCrmIntegration();
  const [audioUrl, setaudioUrl] = useState([]);
  const [audioBlob, setaudioBlob] = useState(null);
  const [audioLoader, setaudioLoader] = useState(false);
  const [createNote, { loading: createNoteLoader }] = useMutation(CREATE_NOTE);
  const [updateNote, { loading: updateNoteLoader }] = useMutation(UPDATE_NOTE);
  const [notesData, setnotesData] = useState({ ...initialNotes, ...leadNote });
  const close = () => {
    setleadNotesData({ ...leadNotesData, open: false });
    // setnotesData(initialNotes);
    // form.resetFields();
  };
  const discardDraft = () => {
    setleadNotesData({ open: false, leadNote: null });
    setnotesData({ ...initialNotes, ...leadNote });
    form.resetFields();
    setformChanged(false);
  };
  useEffect(() => {
    discardDraft();
  }, [selectedLead.id]);
  useEffect(() => {
    if (leadNote?.voiceNotes?.length > 0) {
      setaudioUrl(leadNote?.voiceNotes?.map(item => item?.voiceNote));
    }
  }, [leadNote?.voiceNotes]);

  useEffect(() => {
    if (mode === "EDIT") {
      setnotesData({ ...leadNote });
    }
  }, [leadNote, mode]);
  const setOppDropdown = () => {
    const latestOpportunity =
      selectedLead?.leadOpportunities &&
      selectedLead?.leadOpportunities.length > 0 &&
      selectedLead?.leadOpportunities?.reduce((latest, current) => {
        return dayjs(current.updatedDate).isAfter(dayjs(latest.updatedDate)) ? current : latest;
      });
    const oppoDropdown =
      selectedLead?.leadOpportunities?.length > 0 &&
      selectedLead?.leadOpportunities.map(opp => {
        return {
          id: opp.id,
          name: opp.name,
          opprId: OppId(opp, crmIntegration),
        };
      });
    if (latestOpportunity) {
      form.setFieldValue("leadOpportunityId", latestOpportunity.id);
    }
    return oppoDropdown || [];
  };
  useEffect(() => {
    if (mode === "EDIT") {
      form.setFieldsValue({
        ...leadNote,
        leadOpportunityId: leadNote?.leadOpportunity?.id || null,
      });
      if (!isEmpty(leadNote.leadOpportunity)) {
        setnotesData({ ...notesData, noteType: "OPPORTUNITY" });
      }
    }
  }, [leadNote, mode]);
  const handleSave = async values => {
    const payload = {
      ...values,
      leadId: selectedLead.id,
      userId: user.id,
    };
    const urls = await uploadFilesFromBlobUrls(
      audioUrl.filter(item => item.includes("localhost")),
      `${selectedLead?.fullName || "N/A"}-${dayjs().toISOString()}`,
      user.id,
      selectedLead.id,
      setaudioLoader
    );
    console.log(urls);
    if (mode === "ADD") {
      const res = await createNote({
        variables: {
          ...payload,
          voiceNotes: [...urls, ...audioUrl.filter(item => !item.includes("localhost"))].map(
            item => ({ voiceNote: item })
          ),
        },
      });
      if (res.data.createNote.statusCode === 200) {
        message.success(res.data.createNote.message);
        dispatch(addNote(res.data.createNote.leadNote));
        close();
      } else {
        message.error(res.data.createNote.message);
      }
    } else {
      const res = await updateNote({
        variables: {
          id: leadNote.id,
          ...payload,
          voiceNotes: [...urls, ...audioUrl.filter(item => !item.includes("localhost"))].map(
            item => ({ voiceNote: item })
          ),
        },
      });
      if (res.data.updateNote.statusCode === 200) {
        message.success(res.data.updateNote.message);

        dispatch(updateLeadNoteRedux(res.data.updateNote.leadNote));
        close();
      } else {
        message.error(res.data.updateNote.message);
      }
    }
  };
  useEffect(() => {
    form.setFieldsValue(notesData);
  }, [notesData]);
  return (
    <Drawer
      open={leadNotesData.open}
      className="normal-popup"
      width={isMobileView ? "100%" : "40%"}
      title={`${capitalize(mode)} Note for ${
        leadNote?.leadOpportunity?.name || leadNotesData?.selectedLead?.fullName || ""
      }`}
      onClose={close}
      footer={
        mode !== "VIEW" ? (
          <div className="flex-row-centered gap-2 items-center">
            <Button
              loading={createNoteLoader || updateNoteLoader || audioLoader}
              type="primary"
              className="bg-grey-700"
              onClick={() => form.submit()}
            >
              Save
            </Button>
            <Button onClick={close}>Cancel</Button>
            {formChanged && (
              <Button type="link" onClick={discardDraft}>
                Discard draft
              </Button>
            )}
          </div>
        ) : null
      }
    >
      <div className="h-full w-full overflow-y-auto no-scroll">
        <div>
          {mode !== "VIEW" ? (
            <Form
              onFinish={values => handleSave({ ...values, ...notesData })}
              layout="vertical"
              onFieldsChange={() => setformChanged(true)}
              form={form}
            >
              <div className="flex-row-centered gap-2 items-center justify-between">
                <Form.Item name="noteType" label="Note Type" className="w-full">
                  <Select
                    name="noteType"
                    options={[
                      { label: "Lead Note", value: "LEAD" },
                      { label: "Opportunity Note", value: "OPPORTUNITY" },
                    ]}
                    disabled={mode === "EDIT"}
                    placeholder="Note Type"
                    onSelect={value => setnotesData({ ...notesData, noteType: value })}
                  />
                </Form.Item>
                {notesData.noteType === "OPPORTUNITY" && (
                  <Form.Item
                    rules={[{ required: true, message: "Please select Opportunity" }]}
                    name="leadOpportunityId"
                    className="w-full"
                    label="Select Opportunity"
                  >
                    <Select
                      options={setOppDropdown().map(item => ({
                        label: (
                          <span className="line-clamp-1 block truncate">
                            {`${item?.name || ""} #(${item?.opprId || item?.id || ""})`.substring(
                              0,
                              30
                            )}
                          </span>
                        ),
                        value: item.id,
                      }))}
                      disabled={mode === "EDIT"}
                      placeholder="Select Opportunity"
                      name="leadOpportunityId"
                    />
                  </Form.Item>
                )}
              </div>
              {audioUrl.length > 0 && (
                <Collapse className="transparent-collapse w-full rounded-none">
                  <Collapse.Panel
                    header={<span className="text-xs font-[600] text-grey-700">Recordings</span>}
                    className="lead-profile-sections"
                  >
                    {audioUrl.map((url, index) => (
                      <AudioPlayer
                        key={index}
                        showClose
                        onClose={() =>
                          setaudioUrl(prev => {
                            return prev.slice(0, index);
                          })
                        }
                        src={url}
                      />
                    ))}
                  </Collapse.Panel>
                </Collapse>
              )}
              <Form.Item
                // rules={[{ required: true, message: "Lead Note is required" }]}
                name="note"
                label="Note"
                className="relative w-full h-[57vh]"
              >
                <RichTextEditorWithAudioRecorder
                  content={notesData.note}
                  setcontent={value => {
                    setformChanged(true);
                    setnotesData({ ...notesData, note: value });
                  }}
                  leadNote={leadNote}
                  audioUrl={audioUrl}
                  setAudioUrl={setaudioUrl}
                  audioBlob={audioBlob}
                  setaudioBlob={setaudioBlob}
                  mode={mode}
                  showColorPicker={false}
                />
              </Form.Item>
            </Form>
          ) : (
            <div className="flex-col-centered items-start gap-2">
              <div className="flex-col-centered items-start gap-1">
                <span>Note</span>
              </div>
              <div
                className="w-full border-gray-200 border-solid border-[2px] p-2 pl-[25px] h-[70vh] overflow-y-auto break-words overflow-x-hidden"
                dangerouslySetInnerHTML={{ __html: leadNote.note }}
              />
              <span className="text-xs ">
                By: {leadNote?.updatedBy?.fullName || leadNote?.createdBy?.fullName} -{" "}
                {convertUtcToLocal(
                  leadNote?.updateTs || leadNote?.createTs,
                  "MM/DD/YYYY hh:mm:ss A"
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
